import { EventInput } from '@fullcalendar/core'
import {
  Event,
  CalendarEvent,
  ExtendedEvent,
  ProcessedGame,
  Game,
  EventMetadata,
  Profile,
} from '../types'

/**
 * Adjusts a hex color brightness by the given percentage
 * @param hex Hex color code (e.g., '#15803d')
 * @param percent Percentage to adjust (-100 to 100)
 * @returns Adjusted hex color
 */
const adjustColorBrightness = (hex: string, percent: number): string => {
  // Remove the # if present
  let cleanHex = hex.startsWith('#') ? hex.slice(1) : hex

  // Convert to RGB
  let r = parseInt(cleanHex.substring(0, 2), 16)
  let g = parseInt(cleanHex.substring(2, 4), 16)
  let b = parseInt(cleanHex.substring(4, 6), 16)

  // Adjust brightness
  r = Math.min(255, Math.max(0, Math.round(r + (r * percent) / 100)))
  g = Math.min(255, Math.max(0, Math.round(g + (g * percent) / 100)))
  b = Math.min(255, Math.max(0, Math.round(b + (b * percent) / 100)))

  // Convert back to hex
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b
    .toString(16)
    .padStart(2, '0')}`
}

export const processGameData = (game: Game): ProcessedGame => {
  return {
    ...game,
    thumbnail_url: game.image_url,
  }
}

export const formatImageUrl = (url: string | null | undefined): string => {
  if (!url) return '/placeholder-game.png'

  if (url.startsWith('http:')) {
    return url.replace('http:', 'https:')
  } else if (!url.startsWith('http') && !url.startsWith('/')) {
    return `https:${url}`
  }

  return url
}

export const extractParticipantCount = (
  participantCount: Array<{ count: number }> | { count: number } | number | undefined
): number => {
  if (Array.isArray(participantCount)) {
    return participantCount[0]?.count || 0
  } else if (
    typeof participantCount === 'object' &&
    participantCount !== null &&
    'count' in participantCount
  ) {
    return participantCount.count
  } else if (typeof participantCount === 'number') {
    return participantCount
  }
  return 0
}

export const getGameName = (event: ExtendedEvent): string => {
  const { extendedProps } = event

  if (extendedProps?.metadata?.isPlaceholder) {
    return extendedProps?.metadata?.game?.name || ''
  }

  return extendedProps?.game?.name || ''
}

export const getGameImageUrl = (event: ExtendedEvent): string => {
  const { extendedProps } = event

  if (extendedProps?.isPlaceholder && extendedProps?.metadata?.game?.image_url) {
    return formatImageUrl(extendedProps.metadata.game.image_url)
  }

  if (extendedProps?.gameImageUrl) {
    return formatImageUrl(extendedProps.gameImageUrl)
  } else if (extendedProps?.game?.image_url) {
    return formatImageUrl(extendedProps.game.image_url)
  } else if (extendedProps?.game?.thumbnail_url) {
    return formatImageUrl(extendedProps.game.thumbnail_url)
  }

  const metadata = extendedProps?.metadata as EventMetadata | undefined
  if (metadata?.game?.image_url && !extendedProps?.isPlaceholder) {
    return formatImageUrl(metadata.game.image_url)
  } else if (metadata?.game?.thumbnail_url && !extendedProps?.isPlaceholder) {
    return formatImageUrl(metadata.game.thumbnail_url)
  }

  return '/placeholder-game.png'
}

export const filterEventsByMeetupId = (events: Event[], meetupId: string): Event[] => {
  return events.filter(event => (event as CalendarEvent).meetup_id === meetupId)
}

export const transformEventsForCalendar = (events: Event[]): EventInput[] => {
  return events.map(event => {
    const eventWithExtras = event as CalendarEvent
    const participantCount = extractParticipantCount(eventWithExtras.participant_count)

    // Use event's color if available, otherwise use default green
    const backgroundColor = event.color || '#15803d'
    const borderColor = event.color ? adjustColorBrightness(event.color, -20) : '#166534'
    const textColor = '#ffffff'

    const start = new Date(event.start_time)
    const end = event.end_time ? new Date(event.end_time) : undefined

    const processedGame = event.game ? processGameData(event.game) : null

    const hostId = eventWithExtras.host?.id || event.host_id || null

    return {
      id: event.id,
      title: event.title,
      start,
      end,
      backgroundColor,
      borderColor,
      textColor,
      extendedProps: {
        description: event.description || '',
        location: event.location || '',
        address: event.address || '',
        host: eventWithExtras.host || null,
        host_id: hostId,
        minPlayers: event.min_players,
        maxPlayers: event.max_players,
        skillLevel: event.skill_level,
        isPrivate: event.is_private,
        participantCount,
        game: processedGame,
        meetupId: event.meetup_id || null,
        metadata: event.metadata,
        gameImageUrl: event.game?.image_url || event.metadata?.game?.image_url,
      },
    }
  })
}

export const calculateAvailableSlots = (
  maxPlayers: number | undefined,
  currentPlayers: number
): number => {
  if (!maxPlayers) return 0
  return Math.max(0, maxPlayers - currentPlayers)
}

export const formatTimeRange = (start: Date | string | null, end: Date | string | null): string => {
  if (!start) return ''

  const startTime =
    start instanceof Date
      ? start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : new Date(start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

  if (!end) return startTime

  const endTime =
    end instanceof Date
      ? end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : new Date(end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

  return `${startTime} - ${endTime}`
}

export const processEvents = (events: Event[]): ExtendedEvent[] => {
  return events.map(event => {
    const processedGame = event.game ? processGameData(event.game) : undefined
    return {
      ...event,
      game: processedGame,
    } as ExtendedEvent
  })
}

export const preloadImages = (
  imageUrls: string[],
  preloadedImages: Set<string> = new Set()
): Set<string> => {
  const validUrls = imageUrls.filter(url => url && url !== '/placeholder-game.png')
  const uniqueUrls = [...new Set(validUrls)]

  const newUrls = uniqueUrls.filter(url => !preloadedImages.has(url))
  if (newUrls.length === 0) return preloadedImages

  const updatedPreloadedImages = new Set(preloadedImages)

  newUrls.forEach(url => {
    const img = new Image()
    img.src = url
    updatedPreloadedImages.add(url)
  })

  return updatedPreloadedImages
}

export const extractImageUrlsFromEvents = (events: EventInput[]): string[] => {
  return events.map(event => (event.extendedProps as any)?.gameImageUrl).filter(Boolean)
}

export const getCurrentPlayerCount = (event: ExtendedEvent): number => {
  const { extendedProps } = event

  if (!extendedProps) return 0

  if (typeof extendedProps.participantCount === 'number') {
    return extendedProps.participantCount
  } else if (
    typeof extendedProps.participantCount === 'object' &&
    extendedProps.participantCount !== null
  ) {
    if ('count' in (extendedProps.participantCount as any)) {
      return (extendedProps.participantCount as any).count
    }
  }

  return 0
}

export const getCapacityStatusColors = (
  currentPlayers: number,
  maxPlayers: number
): { bg: string; text: string } => {
  if (currentPlayers >= maxPlayers) {
    return { bg: 'bg-red-900', text: 'text-red-200' }
  } else if (currentPlayers >= maxPlayers * 0.8) {
    return { bg: 'bg-yellow-700', text: 'text-yellow-100' }
  } else {
    return { bg: 'bg-green-800', text: 'text-green-100' }
  }
}
